import {computed, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useFormat, useLogger} from '#imports';
import {withCleanup} from '@malt/utils';
import type {Currency, ProjectInvoiceListResource, ProjectInvoiceResourceTypeEnum} from '@api';
import {projectInformationEndpoints} from '~/registry';
import type {FormattedInvoiceTableItem} from '../types';
import {useProjectInformationStore} from '@/common/stores';

export const useInvoiceStore = defineStore('invoice', () => {
    const logger = useLogger();
    const {formatAmount, formatDateToShort} = useFormat();

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const isLoading = ref(false);
    const projectInvoices = ref<ProjectInvoiceListResource | null>(null);

    const allInvoices = computed(() => projectInvoices.value?.invoices);

    async function fetchInvoices() {
        try {
            isLoading.value = true;
            projectInvoices.value = (await projectInformationEndpoints.getProjectInvoiceList({
                projectId: projectInformation.value.projectId,
            })) as ProjectInvoiceListResource;
        } catch (e) {
            logger.error(e);
            projectInvoices.value = null;
        } finally {
            isLoading.value = false;
        }
    }

    function formatAmountWithDecimals(currency: Currency, amount: number) {
        return !isNaN(amount) ? formatAmount(currency, amount, {decimals: 2}) : '-';
    }

    function createInvoiceUrl(type: ProjectInvoiceResourceTypeEnum, invoiceId: string, lotId?: string) {
        if (type === 'EXPENSE_INVOICE') {
            return `/invoice/pdf/${invoiceId}?withAppendices=true`;
        } else if (
            projectInformation.value.contract.invoicingMode === 'TRIPARTITE' ||
            projectInformation.value.contract.invoicingMode === 'SUBCONTRACTING'
        ) {
            return `/invoice/pdf/${invoiceId}?withAppendices=true`;
        } else if (projectInformation.value.contract.type === 'FIXED_PRICE') {
            return `/project-invoices/bundle/pdf/${projectInformation.value.projectId}?withAppendices=true`;
        } else {
            return lotId ? `/project-invoices/bundle/pdf/${projectInformation.value.projectId}/lot/${lotId}?withAppendices=true` : '';
        }
    }

    const formattedInvoices = computed<FormattedInvoiceTableItem[]>(() => {
        return (
            allInvoices.value?.map((invoice) => {
                return {
                    id: invoice.invoiceId,
                    type: invoice.type,
                    name: invoice.name,
                    date: invoice.issueDate ? formatDateToShort(invoice.issueDate) : '-',
                    rawDate: invoice.issueDate,
                    total: invoice.totalAmount ? formatAmountWithDecimals(projectInformation.value.contract.currency, invoice.totalAmount) : '-',
                    rawTotal: invoice.totalAmount,
                    status: invoice.paymentStatus,
                    action: 'VIEW',
                    pdfUrl: createInvoiceUrl(invoice.type, invoice.invoiceId, invoice.lotId),
                } satisfies FormattedInvoiceTableItem;
            }) ?? []
        );
    });

    return withCleanup({
        fetchInvoices,
        isLoading,
        formattedInvoices,
    });
});
